// @ubm/ui/overlays — LA MÉCANIQUE DE FOCUS, EN FONCTIONS PURES.
//
// Pourquoi pure, et pourquoi ici : le piège de focus est LE manque nommé par l'audit anti-réinvention
// (`membre-navigation-composition-spec.md` §10) — « l'admin a déjà un panneau flottant (portail, Échap,
// clic extérieur, cadrage), un panneau latéral avec fermeture animée et un crochet d'ouverture ; il manque
// PARTOUT le piège de focus ». Trois surfaces le redisaient chacune de leur côté :
//   · `@ubm/product-ui` `primitives/Dialog.tsx` : « Focus-trap réel + portail = couche hardening ultérieure » ;
//   · `@ubm/product-ui` `chrome/SidePeek.tsx` : Échap et fermeture animée, aucun piège ;
//   · `@ubm/member-ui` `shell/SommaireRail.tsx` : `role="dialog"` sans Échap, sans piège, sans portail.
//
// La décision de tabulation est isolée ICI, sans DOM et sans React, pour une raison mesurée : un piège de
// focus « prouvé » par un banc qui poserait lui-même le focus ne prouverait rien (« un oracle qui fabrique
// son entrée ne prouve rien »). En séparant la DÉCISION (ce fichier) du CÂBLAGE (`useOverlayAccessible`),
// la décision se teste exhaustivement sur des listes, et le câblage se teste au clavier réel.

/**
 * Le sélecteur des éléments qui peuvent recevoir le focus au clavier.
 *
 * `[tabindex="-1"]` est EXCLU volontairement : un élément à tabindex négatif est focalisable par programme
 * (c'est ce qui sert de point de chute quand un panneau n'a aucun contrôle) mais il n'est PAS dans l'ordre
 * de tabulation. L'y inclure ferait tabuler vers le conteneur lui-même, c'est-à-dire nulle part.
 */
export const SELECTEUR_FOCALISABLE = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "audio[controls]",
  "video[controls]",
  "iframe",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** Un élément est-il réellement atteignable (ni caché, ni retiré de l'arbre d'accessibilité) ? */
function atteignable(el: HTMLElement): boolean {
  if (el.hasAttribute("disabled")) return false;
  // 🔴 `tabindex="-1"` doit être écarté ICI, et pas seulement par le sélecteur : un `<button tabindex="-1">`
  // entre par `button:not([disabled])` quoi qu'on écrive dans la clause `[tabindex]`. Mesuré au banc — le
  // bouton « hors ordre » se retrouvait dans la liste de tabulation alors qu'il n'y est jamais.
  if (el.getAttribute("tabindex") === "-1") return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.closest("[inert]") !== null) return false;
  if (el.closest("[aria-hidden='true']") !== null) return false;
  // `hidden` et `display:none` : happy-dom comme les navigateurs rendent `offsetParent === null`, mais
  // `offsetParent` vaut aussi `null` pour un `position: fixed` VISIBLE — c'est exactement le cas d'un
  // overlay. On ne s'en sert donc PAS : seul l'attribut `hidden` est lu, le reste relève du CSS que le
  // DOM de test ne calcule pas de toute façon.
  return !el.hasAttribute("hidden");
}

/** Les éléments focalisables d'un conteneur, dans l'ordre du document. */
export function elementsFocalisables(racine: HTMLElement): HTMLElement[] {
  return Array.from(racine.querySelectorAll<HTMLElement>(SELECTEUR_FOCALISABLE)).filter(atteignable);
}

/**
 * LA DÉCISION DE TABULATION — où va le focus quand on presse Tab (ou Maj+Tab) dans un overlay piégé.
 *
 * Invariant : le retour est TOUJOURS un élément de `focalisables`, ou `null` quand la liste est vide. C'est
 * ce qui fait le piège : il n'existe aucune entrée pour laquelle la sortie est en dehors de la liste.
 *
 * - focus hors de la liste (ou nul) → on rentre par le premier (ou le dernier en arrière) ;
 * - dernier élément + Tab → premier (bouclage) ;
 * - premier élément + Maj+Tab → dernier (bouclage).
 */
export function cibleTabulation(
  focalisables: readonly HTMLElement[],
  actif: HTMLElement | null,
  versArriere: boolean,
): HTMLElement | null {
  if (focalisables.length === 0) return null;
  const dernier = focalisables.length - 1;
  const position = actif === null ? -1 : focalisables.indexOf(actif);
  if (position === -1) return versArriere ? focalisables[dernier]! : focalisables[0]!;
  const suivant = versArriere ? position - 1 : position + 1;
  if (suivant < 0) return focalisables[dernier]!;
  if (suivant > dernier) return focalisables[0]!;
  return focalisables[suivant]!;
}

/**
 * Le point de chute à l'OUVERTURE : le premier focalisable, ou le conteneur lui-même.
 *
 * Renvoyer le conteneur n'est utile que s'il est focalisable par programme — l'appelant lui pose
 * `tabindex="-1"`. Sans ce repli, ouvrir un panneau sans aucun contrôle laisserait le focus DERRIÈRE
 * l'overlay, c'est-à-dire sur une page que le lecteur d'écran ne doit plus atteindre.
 */
export function premierFocus(racine: HTMLElement): HTMLElement {
  const explicite = racine.querySelector<HTMLElement>("[data-ubm-overlay-focus]");
  if (explicite !== null && atteignable(explicite)) return explicite;
  return elementsFocalisables(racine)[0] ?? racine;
}
